import type { JsonSchemaDefinition } from "./schemaFormModel";

export type SchemaMessageVariant = "info" | "success" | "warning" | "error";

export interface SchemaUiMessage {
	markdown: string;
	variant?: SchemaMessageVariant;
	before?: string;
	after?: string;
}

const variants = ["info", "success", "warning", "error"];

function isRecord(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === "object" && !Array.isArray(value);
}

function getUi(schema: JsonSchemaDefinition): Record<string, unknown> {
	const ui = schema.metadata?.ui;
	return isRecord(ui) ? ui : {};
}

export function getMarkdownDescription(schema: JsonSchemaDefinition): string | undefined {
	const description = getUi(schema).description;
	return isRecord(description) && description.format === "markdown"
		&& typeof schema.description === "string" && schema.description.trim()
		? schema.description
		: undefined;
}

export function usesMarkdownDescription(schema: JsonSchemaDefinition): boolean {
	const description = getUi(schema).description;
	return isRecord(description) && description.format === "markdown";
}

export function usesFullWidth(schema: JsonSchemaDefinition): boolean {
	return getUi(schema).width === "full";
}

function getMessageErrors(message: unknown, schema: JsonSchemaDefinition): string[] {
	if(!isRecord(message)) {
		return ["must be a message object."];
	}
	const errors: string[] = [];
	if(typeof message.markdown !== "string" || !message.markdown.trim()) {
		errors.push("markdown must be a non-empty string.");
	}
	if(message.variant !== undefined && !variants.includes(message.variant as string)) {
		errors.push("variant must be info, success, warning, or error (or omitted for neutral).");
	}
	if("before" in message && "after" in message) {
		errors.push("must specify at most one of before or after.");
	}
	for(const anchor of ["before", "after"]) {
		if(anchor in message && (typeof message[anchor] !== "string"
			|| !Object.prototype.hasOwnProperty.call(schema.properties ?? {}, message[anchor] as string))) {
			errors.push(`${anchor} must name a direct property of the containing object.`);
		}
	}
	return errors;
}

export function getSchemaMessages(schema: JsonSchemaDefinition): SchemaUiMessage[] {
	const messages = getUi(schema).messages;
	return Array.isArray(messages)
		? messages.filter(message => getMessageErrors(message, schema).length === 0) as SchemaUiMessage[]
		: [];
}

// Presentation errors deliberately stay outside validateJsonSchemaModel and ngModel validity.
export function getSchemaUiConfigurationErrors(schema: JsonSchemaDefinition, path = "#"): string[] {
	const errors: string[] = [];
	const messages = getUi(schema).messages;
	if(messages !== undefined) {
		const types = Array.isArray(schema.type) ? schema.type : [schema.type];
		if(!types.includes("object") && !schema.properties) {
			errors.push(`${path}/metadata/ui/messages: messages must be declared on an object schema.`);
		}
		if(!Array.isArray(messages)) {
			errors.push(`${path}/metadata/ui/messages: must be an array.`);
		} else {
			messages.forEach((message, index) => {
				getMessageErrors(message, schema).forEach(error => {
					errors.push(`${path}/metadata/ui/messages/${index}: ${error}`);
				});
			});
		}
	}
	for(const [key, property] of Object.entries(schema.properties ?? {})) {
		const pointerKey = key.replace(/~/g, "~0").replace(/\//g, "~1");
		errors.push(...getSchemaUiConfigurationErrors(property, `${path}/properties/${pointerKey}`));
	}
	if(schema.items) {
		errors.push(...getSchemaUiConfigurationErrors(schema.items, `${path}/items`));
	}
	return errors;
}
